import { MessageOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Flex,
  Segmented,
  Spin,
  Statistic,
  Tag
} from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../../components/EntityTable/EntityTable.tsx"
import { SearchBar } from "../../../../components/Input/SearchBar.tsx"
import {
  type SmsUsageFigures,
  type SmsUsageGrouping,
  type SmsUsageReport,
  type SmsUsageRow,
  getSmsUsage
} from "../../api/sms-usage.services.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"

const DATE = "YYYY-MM-DD"
const SEARCH_FROM_ROWS = 20

const money = (value: string, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  }).format(Number(value))

const share = (value: string | null) =>
  value === null ? "—" : `${(Number(value) * 100).toFixed(1)}%`

function D23Tag({ figures }: { figures: SmsUsageFigures }) {
  const { t } = useTranslation()
  if (!figures.d23.timed) return <Tag>{t("smsUsage.d23.noData")}</Tag>
  return figures.d23.target_missed ? (
    <Tag color="red">{t("smsUsage.d23.missed")}</Tag>
  ) : (
    <Tag color="green">{t("smsUsage.d23.met")}</Tag>
  )
}

/** Volume, cost, opt-outs and D23 latency of the texts this company (or, for
 *  a platform admin, every company) sent in a period. */
export function SmsUsagePanel({ platform }: { platform: boolean }) {
  const { t, i18n } = useTranslation()
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(29, "day"),
    dayjs()
  ])
  const [groupBy, setGroupBy] = useState<SmsUsageGrouping>("day")
  const [report, setReport] = useState<SmsUsageReport | null>(null)
  const [loadState, setLoadState] = useState<"loading" | "loaded" | "error">(
    "loading"
  )
  const [attempt, setAttempt] = useState(0)
  const [search, setSearch] = useState("")

  const reportKey = `${groupBy}:${range[0].format(DATE)}:${range[1].format(DATE)}`

  useEffect(() => {
    setSearch("")
  }, [reportKey])

  useEffect(() => {
    let cancelled = false
    setLoadState("loading")
    void getSmsUsage({
      start: range[0].format(DATE),
      end: range[1].format(DATE),
      group_by: groupBy
    }).then((response) => {
      if (cancelled) return
      if (response?.ok && response.data) {
        setReport(response.data)
        setLoadState("loaded")
      } else {
        setReport(null)
        setLoadState("error")
      }
    })
    return () => {
      cancelled = true
    }
  }, [range, groupBy, attempt])

  const groupings = useMemo(
    () =>
      (platform
        ? (["day", "type", "company", "number"] as const)
        : (["day", "type"] as const)
      ).map((value) => ({ value, label: t(`smsUsage.groupBy.${value}`) })),
    [platform, t]
  )

  // Label rows by the grouping they were counted under, not the one being loaded.
  const shownGrouping = report?.group_by ?? groupBy

  const keyLabel = (row: SmsUsageRow) => {
    switch (shownGrouping) {
      case "type":
        return row.key.label ?? t("smsUsage.unknownType")
      case "company":
        return row.key.team__name ?? t("smsUsage.noCompany")
      case "number":
        return row.key.from_number || "—"
      default:
        return dayjs(row.key.day).format("MMM D, YYYY")
    }
  }

  const columns = [
    {
      key: "key",
      title: t(`smsUsage.groupBy.${shownGrouping}`),
      render: (_: unknown, row: SmsUsageRow) => keyLabel(row)
    },
    { key: "accepted", dataIndex: "accepted", title: t("smsUsage.sent") },
    {
      key: "delivered",
      dataIndex: "delivered",
      title: t("smsUsage.delivered")
    },
    {
      key: "failed",
      title: t("smsUsage.failed"),
      render: (_: unknown, row: SmsUsageRow) => row.undelivered + row.failed
    },
    { key: "skipped", dataIndex: "skipped", title: t("smsUsage.notSent") },
    {
      key: "cost",
      title: t("smsUsage.cost"),
      render: (_: unknown, row: SmsUsageRow) => money(row.cost, i18n.language)
    },
    {
      key: "d23",
      title: t("smsUsage.d23.column"),
      render: (_: unknown, row: SmsUsageRow) => (
        <Flex gap={6} align="center">
          <span>{share(row.d23.within_target_share)}</span>
          <D23Tag figures={row} />
        </Flex>
      )
    },
    {
      key: "slowest",
      title: t("smsUsage.slowest"),
      render: (_: unknown, row: SmsUsageRow) =>
        row.d23.timed
          ? t("smsUsage.seconds", { count: row.slowest_seconds })
          : "—"
    }
  ]

  const totals = report?.totals
  const rows = report?.rows ?? []
  const needle = search.trim().toLowerCase()
  const shownRows = needle
    ? rows.filter((row) => String(keyLabel(row)).toLowerCase().includes(needle))
    : rows

  return (
    <SettingsCard
      iconWrapperClassName="bg-blue-100"
      icon={<MessageOutlined className="text-blue-600" />}
      title={t(platform ? "smsUsage.titlePlatform" : "smsUsage.title")}
      subtitle={t("smsUsage.subtitle")}
    >
      <Flex vertical gap="middle">
        <Flex gap="middle" wrap align="center">
          <DatePicker.RangePicker
            allowClear={false}
            value={range}
            aria-label={t("smsUsage.period")}
            onChange={(value) => {
              if (value?.[0] && value?.[1]) setRange([value[0], value[1]])
            }}
          />
          <Segmented
            aria-label={t("smsUsage.groupByLabel")}
            options={groupings}
            value={groupBy}
            onChange={(value) => setGroupBy(value as SmsUsageGrouping)}
          />
        </Flex>

        {loadState === "error" && (
          <Alert
            type="error"
            showIcon
            message={t("smsUsage.loadError")}
            action={
              <Button size="small" onClick={() => setAttempt((n) => n + 1)}>
                {t("smsUsage.retry")}
              </Button>
            }
          />
        )}

        <Spin spinning={loadState === "loading"}>
          {totals && (
            <Flex gap="large" wrap>
              <Statistic title={t("smsUsage.sent")} value={totals.accepted} />
              <Statistic
                title={t("smsUsage.delivered")}
                value={totals.delivered}
              />
              <Statistic
                title={t("smsUsage.failed")}
                value={totals.undelivered + totals.failed}
              />
              <Statistic title={t("smsUsage.notSent")} value={totals.skipped} />
              <Statistic
                title={t("smsUsage.cost")}
                value={money(totals.cost, i18n.language)}
              />
              <Statistic
                title={t("smsUsage.optOuts")}
                value={t("smsUsage.optOutsValue", {
                  stopped: report?.opt_outs.stopped ?? 0,
                  started: report?.opt_outs.started ?? 0
                })}
              />
              <Statistic
                title={t("smsUsage.d23.title")}
                valueRender={() => (
                  <Flex gap={8} align="center">
                    <span>{share(totals.d23.within_target_share)}</span>
                    <D23Tag figures={totals} />
                  </Flex>
                )}
              />
              {report?.queue && (
                <Statistic
                  title={t("smsUsage.queue.title")}
                  value={
                    report.queue.waiting
                      ? t("smsUsage.queue.waiting", {
                          count: report.queue.waiting,
                          time: dayjs(report.queue.clears_at).format("HH:mm")
                        })
                      : t("smsUsage.queue.empty")
                  }
                />
              )}
            </Flex>
          )}
        </Spin>
        <p className="m-0 text-sm text-gray-500">{t("smsUsage.d23.hint")}</p>

        {(rows.length > SEARCH_FROM_ROWS || needle) && (
          <SearchBar
            key={reportKey}
            allowClear
            className="max-w-xs"
            placeholder={t("smsUsage.search")}
            aria-label={t("smsUsage.search")}
            onSearch={(value) => setSearch(value ?? "")}
          />
        )}
        <EntityTable<SmsUsageRow>
          surface="settings-sms-usage"
          tab={platform ? "platform" : "company"}
          size="small"
          rowKey={(row) => JSON.stringify(row.key)}
          columns={columns}
          dataSource={shownRows}
          withOutPagination
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("smsUsage.empty")}
              />
            )
          }}
        />
      </Flex>
    </SettingsCard>
  )
}
